/**
 * The only translation of appearance blocks into CSS.
 *
 * Inputs are typed values (validated by the server and re-validated here: a color that is not
 * `#rrggbb` is ignored, numbers are clamped), and outputs are CSS custom properties plus
 * `data-look` attributes. Nothing user-provided is ever interpolated as a free string.
 */
import type { CSSProperties } from 'react';

import { contrastText, readableOnAll } from '@/shared/lib/color';

import type { Background, Gradient, Look, LookStyle, Pattern } from './types';

const HEX = /^#[0-9a-f]{6}$/i;

export function isHex(value: string | null | undefined): value is string {
  return typeof value === 'string' && HEX.test(value);
}

const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));

export function hexToRgba(hex: string, alpha: number): string {
  const channel = (offset: number) => parseInt(hex.slice(offset, offset + 2), 16);
  return `rgba(${channel(1)}, ${channel(3)}, ${channel(5)}, ${clamp(alpha, 0, 1)})`;
}

/** The color halfway between two `#rrggbb` colors (to pick a readable text color for gradients). */
export function mixHex(a: string, b: string): string {
  const mix = (offset: number) =>
    Math.round(
      (parseInt(a.slice(offset, offset + 2), 16) + parseInt(b.slice(offset, offset + 2), 16)) / 2,
    )
      .toString(16)
      .padStart(2, '0');
  return `#${mix(1)}${mix(3)}${mix(5)}`;
}

export function gradientCss(gradient: Gradient): string | null {
  if (!isHex(gradient.start) || !isHex(gradient.end)) return null;
  return `linear-gradient(${Math.round(clamp(gradient.angle, 0, 360))}deg, ${gradient.start}, ${gradient.end})`;
}

/** Readable text color on a gradient: the one that reads best at its WORST point (both stops and
 * the midpoint), so a light end of a dark gradient is not ignored. */
export function gradientText(gradient: Gradient): '#000000' | '#ffffff' {
  return readableOnAll([gradient.start, mixHex(gradient.start, gradient.end), gradient.end]);
}

const svgUrl = (svg: string) => `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;

export interface PatternLayer {
  image: string;
  size: string;
  /** Behind the pattern; `null` keeps the surface's own background. */
  background: string | null;
}

/** A tiled pattern layer for a preset id; colors are validated, geometry is fixed. */
export function patternLayer(pattern: Pattern): PatternLayer | null {
  if (!isHex(pattern.color)) return null;
  const alpha = clamp(pattern.opacity, 0.05, 0.6);
  const scale = Math.round(clamp(pattern.scale, 1, 3));
  const ink = hexToRgba(pattern.color, alpha);
  const background = isHex(pattern.background) ? pattern.background : null;
  switch (pattern.id) {
    case 'dots': {
      const size = 14 * scale;
      return {
        image: `radial-gradient(circle, ${ink} 1.2px, transparent 1.7px)`,
        size: `${size}px ${size}px`,
        background,
      };
    }
    case 'grid': {
      const size = 16 * scale;
      return {
        image: `linear-gradient(${ink} 1px, transparent 1px), linear-gradient(90deg, ${ink} 1px, transparent 1px)`,
        size: `${size}px ${size}px`,
        background,
      };
    }
    case 'lines': {
      const size = 14 * scale;
      return {
        image: `repeating-linear-gradient(45deg, ${ink} 0 1px, transparent 1px ${size}px)`,
        size: 'auto',
        background,
      };
    }
    case 'waves': {
      const w = 40 * scale;
      const h = 12 * scale;
      const stroke = hexToRgba(pattern.color, alpha);
      const svg =
        `<svg xmlns="http://www.w3.org/2000/svg" width="${w}" height="${h}" viewBox="0 0 40 12">` +
        `<path d="M0 6 Q10 0 20 6 T40 6" fill="none" stroke="${stroke}" stroke-width="1.2"/></svg>`;
      return { image: svgUrl(svg), size: `${w}px ${h}px`, background };
    }
    case 'noise': {
      const channel = (offset: number) =>
        (parseInt(pattern.color.slice(offset, offset + 2), 16) / 255).toFixed(3);
      const svg =
        '<svg xmlns="http://www.w3.org/2000/svg" width="140" height="140">' +
        '<filter id="n"><feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" stitchTiles="stitch"/>' +
        `<feColorMatrix values="0 0 0 0 ${channel(1)} 0 0 0 0 ${channel(3)} 0 0 0 0 ${channel(5)} 0 0 0 ${alpha.toFixed(2)} 0"/></filter>` +
        '<rect width="100%" height="100%" filter="url(#n)"/></svg>';
      return { image: svgUrl(svg), size: `${140 * scale}px ${140 * scale}px`, background };
    }
  }
}

type Vars = Record<string, string>;

function cssVars(vars: Vars): CSSProperties {
  return vars;
}

export interface LookProps {
  'data-look'?: LookStyle;
  /** Advanced (per-element) colors present: flags for the CSS. */
  'data-has-header'?: 'true';
  'data-has-border'?: 'true';
  'data-has-body'?: 'true';
  /** Explicit text colors / content surface (typed colors, re-validated here). */
  'data-has-title'?: 'true';
  'data-has-text'?: 'true';
  'data-has-muted'?: 'true';
  'data-has-surface'?: 'true';
  style?: CSSProperties;
}

/** Custom properties + flags of the explicit text colors and the content surface. */
function textColorProps(look: Look, vars: Vars): Partial<LookProps> {
  const flags: Partial<LookProps> = {};
  if (isHex(look.title_color)) {
    vars['--look-title'] = look.title_color;
    flags['data-has-title'] = 'true';
  }
  if (isHex(look.text_color)) {
    vars['--look-text'] = look.text_color;
    flags['data-has-text'] = 'true';
  }
  if (isHex(look.muted_color)) {
    vars['--look-muted'] = look.muted_color;
    flags['data-has-muted'] = 'true';
  }
  if (isHex(look.surface)) {
    vars['--look-surface'] = look.surface;
    // Text on the panel is readable by default; an explicit text color still wins.
    vars['--look-surface-fg'] = contrastText(look.surface);
    flags['data-has-surface'] = 'true';
  }
  return flags;
}

/**
 * DOM hooks for a look: `data-look` selects the CSS rules, custom properties carry the colors.
 * `null`/`standard` yields nothing, so the element keeps its neutral design.
 */
export function lookProps(look: Look | null | undefined): LookProps {
  if (!look) return {};
  if (look.style === 'standard') {
    // No fill, but explicit text colors / a content panel still apply.
    const vars: Vars = {};
    const flags = textColorProps(look, vars);
    return Object.keys(vars).length > 0 ? { ...flags, style: cssVars(vars) } : {};
  }
  const vars: Vars = {};
  const flags: Partial<LookProps> = {};
  const color = isHex(look.color) ? look.color : null;
  if (color) {
    vars['--look-color'] = color;
    vars['--look-fg'] = contrastText(color);
  }
  if (!look.one_color && look.advanced) {
    if (isHex(look.advanced.header)) {
      vars['--look-header'] = look.advanced.header;
      flags['data-has-header'] = 'true';
    }
    if (isHex(look.advanced.border)) {
      vars['--look-border'] = look.advanced.border;
      flags['data-has-border'] = 'true';
    }
    if (isHex(look.advanced.body)) {
      vars['--look-body'] = look.advanced.body;
      vars['--look-fg'] = contrastText(look.advanced.body);
      flags['data-has-body'] = 'true';
    }
  }
  if (look.style === 'gradient' && look.gradient) {
    const css = gradientCss(look.gradient);
    if (!css) return {};
    vars['--look-gradient'] = css;
    vars['--look-fg'] = gradientText(look.gradient);
  }
  if (look.style === 'pattern' && look.pattern) {
    const layer = patternLayer(look.pattern);
    if (!layer) return {};
    vars['--look-pattern'] = layer.image;
    vars['--look-pattern-size'] = layer.size;
    if (layer.background) {
      vars['--look-pattern-bg'] = layer.background;
      vars['--look-fg'] = contrastText(layer.background);
    }
  }
  Object.assign(flags, textColorProps(look, vars));
  if (Object.keys(vars).length === 0) return {};
  return { 'data-look': look.style, ...flags, style: cssVars(vars) };
}

/** A column's look: its `color` + `appearance_mode` plus the extras, in the same vocabulary. */
export function columnLook(
  color: string | null,
  mode: LookStyle,
  extra: {
    one_color: boolean;
    advanced: Look['advanced'];
    gradient: Gradient | null;
    pattern: Pattern | null;
  } | null,
): Look | null {
  const gradient = extra?.gradient ?? null;
  const pattern = extra?.pattern ?? null;
  if (!color && !gradient && !pattern && !extra?.advanced) return null;
  return {
    v: 1,
    style: mode,
    one_color: extra?.one_color ?? true,
    color,
    advanced: extra?.advanced ?? null,
    gradient: mode === 'gradient' ? gradient : null,
    pattern: mode === 'pattern' ? pattern : null,
  };
}

export interface BackgroundResult {
  style: CSSProperties;
  /** Dark/light overlay strength for image backgrounds (0 = none). */
  overlay: number;
  /** Readable text color to use directly on this background. */
  foreground: '#000000' | '#ffffff' | null;
}

/** CSS for a board / column background; the image URL comes from the API, never from the user. */
export function backgroundStyle(
  background: Background | null | undefined,
  imageUrl: string | null,
): BackgroundResult {
  const none: BackgroundResult = { style: {}, overlay: 0, foreground: null };
  if (!background) return none;
  switch (background.kind) {
    case 'color':
      return isHex(background.color)
        ? {
            style: { backgroundColor: background.color },
            overlay: 0,
            foreground: contrastText(background.color),
          }
        : none;
    case 'gradient': {
      const css = background.gradient ? gradientCss(background.gradient) : null;
      return css && background.gradient
        ? {
            style: { backgroundImage: css },
            overlay: 0,
            foreground: gradientText(background.gradient),
          }
        : none;
    }
    case 'pattern': {
      const layer = background.pattern ? patternLayer(background.pattern) : null;
      if (!layer) return none;
      return {
        style: {
          backgroundImage: layer.image,
          backgroundSize: layer.size,
          ...(layer.background && { backgroundColor: layer.background }),
        },
        overlay: 0,
        foreground: layer.background ? contrastText(layer.background) : null,
      };
    }
    case 'image': {
      // Only API paths or local previews (blob:) are ever used as an image source.
      if (!imageUrl || !(imageUrl.startsWith('/api/') || imageUrl.startsWith('blob:'))) return none;
      const overlay = clamp(background.overlay, 0, 0.8);
      const image = `url("${imageUrl}")`;
      return {
        style: {
          // The overlay is a flat dark layer above the picture: cards and text stay readable.
          backgroundImage:
            overlay > 0
              ? `linear-gradient(rgb(0 0 0 / ${overlay}), rgb(0 0 0 / ${overlay})), ${image}`
              : image,
          backgroundSize: 'cover',
          backgroundPosition: `${Math.round(clamp(background.position_x, 0, 1) * 100)}% ${Math.round(clamp(background.position_y, 0, 1) * 100)}%`,
        },
        overlay,
        foreground: null,
      };
    }
    case 'none':
      return none;
  }
}

export function coverStyle(
  cover:
    | { kind: 'color'; color: string }
    | { kind: 'gradient'; start: string; end: string; angle: number },
): CSSProperties {
  if (cover.kind === 'color') return isHex(cover.color) ? { background: cover.color } : {};
  const css = gradientCss(cover);
  return css ? { background: css } : {};
}
